import { useState, useMemo, useEffect, useRef } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAbortRef, useIsMountedRef, isAbortError } from "@/hooks/use-cancellable-mutation";
import { Send, AlertCircle, Search, X, Sparkles, Loader2, Clock, Paperclip } from "lucide-react";
import { searchIncludes } from "@/lib/search-normalize";
import { Input } from "@/components/ui/input";
import type { Property } from "@shared/schema";
import { BroadcastRecipientPicker, BroadcastFinalRecipientReview, reconcileRecipientSelection, recipientMatches, selectableRecipient, type BroadcastReviewRow } from "@/components/broadcast-recipient-picker";
import { InsertPlaceholderDropdown } from "@/components/insert-placeholder-dropdown";
import { MessagePlaceholderPreview } from "@/components/message-placeholder-preview";
import type { MessageVarContext } from "@/lib/message-placeholders";
import { PadsplitSendErrorDialog, isPadsplitSendError } from "@/components/padsplit-send-error-dialog";
import { MessagingOutageBanner } from "@/components/messaging-outage-banner";
import { useMessagingOutage } from "@/hooks/use-messaging-outage";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PendingAttachment, makeAttachmentId, ALLOWED_ATTACHMENT_MIME, ATTACHMENT_MAX_BYTES, ATTACHMENT_MAX_COUNT, formatBytes } from "@/pages/app-messages/_shared";
import { uploadMimeType } from "@/lib/upload-mime";
import { annotateAttachmentUrl } from "@/lib/attachment-url";
import { ToastAction } from "@/components/ui/toast";
import { BroadcastRosterReviewPanel, useBroadcastRosterReview } from "@/components/broadcast-roster-review";
import { BroadcastStatusDialog, RecentBroadcastsDialog } from "@/components/broadcast-status-dialog";
import { boundedBroadcastRequest, useBroadcastRequestId } from "@/hooks/use-broadcast-request";

interface MessageWholeHouseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MessageWholeHouseModal({ open, onOpenChange }: MessageWholeHouseModalProps) {
  const { toast } = useToast();
  const { enabled: messagingOutage } = useMessagingOutage();
  const [padsplitErrorOpen, setPadsplitErrorOpen] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [propertySearch, setPropertySearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [messageText, setMessageText] = useState("");
  const [audienceMode, setAudienceMode] = useState<"whole_house" | "selected_residents">("whole_house");

  const [finalReviewOpen, setFinalReviewOpen] = useState(false);
  const [finalConfirmation, setFinalConfirmation] = useState<string | null>(null);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ─── Attachments ─────────────────────────────────────────────────────────
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

  // ─── Scheduling ──────────────────────────────────────────────────────────
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);

  // ─── Improve with AI ──────────────────────────────────────────────────────
  // Same house-voice rewrite as the per-member composer, pointed at the
  // broadcast endpoint (org-scoped retrieval + placeholder-token coaching, so
  // the rewrite personalises via {{tokens}} rather than one recipient's
  // literal details). preImproveText holds the pre-rewrite draft so the host
  // can undo until they type.
  const [preImproveText, setPreImproveText] = useState<string | null>(null);
  // Fail CLOSED — the button only appears once the server confirms access.
  const aiDraftingAccessQuery = useQuery<{ allowed: boolean }>({
    queryKey: ["/api/app/ai-drafting/access"],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: open,
  });
  const aiDraftingAllowed = aiDraftingAccessQuery.data?.allowed === true;
  const improveMutation = useMutation({
    mutationFn: (draft: string) =>
      apiRequest("POST", "/api/app/messages/broadcast/improve", { draft }).then(
        (r) => r.json() as Promise<{ improved: string }>,
      ),
    onSuccess: (data, draft) => {
      if (data.improved && String(data.improved).trim()) {
        setPreImproveText(draft);
        setMessageText(data.improved);
      } else {
        toast({
          title: "No improvement returned",
          description: "The AI couldn't improve this draft. Your original text is preserved.",
        });
      }
    },
    onError: (err) => {
      // Never destroys the draft — the host keeps their text either way.
      toast({
        title: "Couldn't improve draft",
        description: parseErrorMessage(err, "AI improvement failed. Your draft is unchanged."),
        variant: "destructive",
      });
    },
  });
  const isImproving = improveMutation.isPending;

  // ─── Attachment handlers ─────────────────────────────────────────────────
  const readyAttachments = attachments.filter((a) => a.status === "ready");
  const hasUploading = attachments.some((a) => a.status === "uploading");
  const hasAttachmentErrors = attachments.some((a) => a.status === "error");

  async function handleFiles(files: FileList | File[] | null) {
    if (!files) return;
    const list = Array.from(files);
    if (list.length === 0) return;
    const remaining = ATTACHMENT_MAX_COUNT - attachments.length;
    if (remaining <= 0) {
      toast({ title: "Attachment limit reached", description: `You can attach up to ${ATTACHMENT_MAX_COUNT} files.`, variant: "destructive" });
      return;
    }
    const incoming = list.slice(0, remaining);
    for (const file of incoming) {
      const id = makeAttachmentId();
      const resolvedMime = uploadMimeType(file);
      if (!ALLOWED_ATTACHMENT_MIME.has(resolvedMime)) {
        setAttachments((prev) => [...prev, { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "error", errorMessage: "Only images and PDFs are allowed" }]);
        continue;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setAttachments((prev) => [...prev, { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "error", errorMessage: `File is over ${Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))}MB` }]);
        continue;
      }
      setAttachments((prev) => [...prev, { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "uploading" }]);
      try {
        const urlRes = await apiRequest("POST", "/api/app/messages/upload-url", {});
        const { uploadUrl, objectPath } = (await urlRes.json()) as { uploadUrl: string; objectPath: string };
        const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": resolvedMime }, body: file });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "ready", objectPath } : a)));
      } catch (err: any) {
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error", errorMessage: parseErrorMessage(err, "Upload failed") } : a)));
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  // ─── Schedule handler ─────────────────────────────────────────────────────
  function handleScheduleConfirm() {
    setScheduleError("");
    if (!scheduleDate) { setScheduleError("Pick a date."); return; }
    const iso = `${scheduleDate}T${scheduleTime || "00:00"}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) { setScheduleError("Invalid date/time."); return; }
    if (dt <= new Date()) { setScheduleError("Schedule time must be in the future."); return; }
    setScheduledFor(dt.toISOString());
    setScheduleOpen(false);
  }

  // Drop the undo affordance once the host edits the improved text (they've
  // accepted it) and whenever the modal is reopened.
  useEffect(() => {
    if (!open) setPreImproveText(null);
  }, [open]);

  function insertAtCursor(text: string) {
    const el = messageTextareaRef.current;
    if (!el) {
      setMessageText((prev) => prev + (prev && !prev.endsWith("\n") ? "\n" : "") + text);
    } else {
      const start = el.selectionStart ?? messageText.length;
      const end = el.selectionEnd ?? messageText.length;
      const current = el.value;
      setMessageText(current.slice(0, start) + text + current.slice(end));
      setTimeout(() => {
        el.focus();
        const newCursor = start + text.length;
        el.setSelectionRange(newCursor, newCursor);
      }, 0);
    }
  }
  const [lastBroadcastId, setLastBroadcastId] = useState<string | null>(null);
  const ambiguousSubmissionRef = useRef(false);
  const submissionBodyRef = useRef<{ id: string; body: Record<string, unknown> } | null>(null);
  const restoreSubmittedDraftRef = useRef<(() => void) | null>(null);
  const submittedBroadcastIdRef = useRef<string | null>(null);
  const [submissionRevision, setSubmissionRevision] = useState(0);

  // Task #2367 — track the in-flight controller so Cancel can abort the
  // broadcast / retry fetch instead of just blocking dismissal.
  const isMountedRef = useIsMountedRef();
  const abort = useAbortRef();

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = useMemo(
    () => (propertiesQuery.data || []).filter(p => p.status !== "archive"),
    [propertiesQuery.data]
  );

  // Filter the visible property list by the search box. Selection state is kept
  // against the full `properties` list, so narrowing the search never drops or
  // changes existing selections. Fields are coerced with `searchIncludes`
  // (String(x ?? "")) so legacy rows with a null name/address can't crash it.
  const visibleProperties = useMemo(() => {
    const q = propertySearch.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter(
      (p) => searchIncludes(p.name, q) || searchIncludes(p.address, q),
    );
  }, [properties, propertySearch]);

  const residencyPollingStartedAt = useRef(Date.now());
  useEffect(() => { residencyPollingStartedAt.current = Date.now(); }, [open, selectedPropertyIds.join(",")]);
  const membersQuery = useQuery<BroadcastReviewRow[]>({
    queryKey: ["/api/app/messages/broadcast/members", selectedPropertyIds],
    queryFn: async () => {
      if (selectedPropertyIds.length === 0) return [];
      const res = await fetch(
        `/api/app/messages/broadcast/members?propertyIds=${selectedPropertyIds.join(",")}`,
        { credentials: "include" }
      );
      // Fail closed: an error must never look like "this house has nobody".
      if (!res.ok) throw new Error(`Couldn't load members (${res.status})`);
      return res.json();
    },
    enabled: selectedPropertyIds.length > 0,
    refetchInterval: query => open && Date.now() - residencyPollingStartedAt.current < 60_000 &&
      query.state.data?.some(row => row.residencyCheckState === "checking") ? 2_000 : false,
  });

  const members = useMemo(() => membersQuery.data || [], [membersQuery.data]);
  const residencyPending = members.some(row => row.residencyCheckState === "checking");
  const residencyUnavailable = members.some(row => row.residencyCheckState === "failed" || row.residencyCheckState === "unavailable");
  const rawMembers = members;

  // Filter the visible member list by the member search box. Selection state is
  // kept against the full `members` list, so narrowing the search never drops or
  // changes existing selections. Name is coerced with `searchIncludes`
  // (String(x ?? "")) so a legacy null name can't crash the list.
  const visibleMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => recipientMatches(m, q));
  }, [members, memberSearch]);

  // Every bulk selection path (select-all, shift-range, the default
  // "everyone") works from this list rather than visibleMembers, so a member
  // the send route would refuse can never be selected in bulk.
  const selectableMemberIds = useMemo(
    () => visibleMembers.filter((m) => m.group === "current" && selectableRecipient(m)).map((m) => m.id),
    [visibleMembers],
  );

  const selectionInitializedRef = useRef(false);
  const previousDirectoryRef = useRef<BroadcastReviewRow[]>([]);
  useEffect(() => {
    if (!membersQuery.isSuccess || membersQuery.isFetching || selectedPropertyIds.length === 0) return;
    const initialized = selectionInitializedRef.current;
    const previous = previousDirectoryRef.current;
    previousDirectoryRef.current = members;
    selectionInitializedRef.current = true;
    setSelectedMemberIds((prev) => {
      const next = reconcileRecipientSelection(members, prev, initialized, previous);
      return next.length === prev.length && next.every((id, index) => id === prev[index]) ? prev : next;
    });
  }, [members, membersQuery.isSuccess, membersQuery.isFetching, selectedPropertyIds.length]);

  useEffect(() => {
    if (selectedPropertyIds.length === 0) {
      setSelectedMemberIds([]);
      selectionInitializedRef.current = false;
      previousDirectoryRef.current = [];
    }
  }, [selectedPropertyIds]);

  const validSelectedIds = useMemo(() => {
    const currentMemberIds = new Set(members.filter(selectableRecipient).map(m => m.id));
    return selectedMemberIds.filter(id => currentMemberIds.has(id));
  }, [selectedMemberIds, members]);

  // ─── Roster safety review (pre-send) ──────────────────────────────────────
  const [reviewing, setReviewing] = useState(false);
  const [statusBroadcastId, setStatusBroadcastId] = useState<string | null>(null);
  const [recentOpen, setRecentOpen] = useState(false);
  // Whole-house checks still examine every room, but consent never expands.
  const excludedMemberIds = useMemo(() => {
    const chosen = new Set(validSelectedIds);
    return rawMembers.filter((m) => !chosen.has(m.id)).map((m) => m.id);
  }, [rawMembers, validSelectedIds]);
  const selectedRecipients = useMemo(() => {
    const selected = new Set(validSelectedIds);
    return members.filter((m) => selected.has(m.id));
  }, [members, validSelectedIds]);
  const reviewedRecipients = selectedRecipients.map(({ id, reviewKey }) => ({ id, reviewKey }));
  const rosterReview = useBroadcastRosterReview({
    active: open && selectedPropertyIds.length > 0 && !membersQuery.isFetching && validSelectedIds.length > 0,
    propertyIds: selectedPropertyIds,
    memberIds: validSelectedIds,
    excludedMemberIds,
    audienceMode,
    reviewedRecipients,
  });
  const statusFilteredMemberIds = useMemo(() => new Set(members.filter((m) => m.group === "excluded").map((m) => m.id)), [members]);
  // Bind confirmation to evidence, the exact selection and all submitted draft fields.
  // Include the full directory so a newly discovered person is visible for renewed review,
  // never silently added to an already reviewed whole-house audience.
  const finalReviewKey = JSON.stringify({
    rows: members.map(({ id, reviewKey }) => ({ id, reviewKey })), selected: reviewedRecipients, selectedPropertyIds, audienceMode,
    messageText, scheduledFor, attachments: readyAttachments,
    rosterFingerprint: rosterReview.data?.fingerprint,
  });
  const finalConfirmed = finalConfirmation === finalReviewKey;
  const clientRequestId = useBroadcastRequestId(JSON.stringify({
    audienceMode,
    messageText,
    propertyIds: [...selectedPropertyIds].sort((a, b) => a - b),
    memberIds: [...validSelectedIds].sort((a, b) => a - b),
    reviewedRecipients: [...reviewedRecipients].sort((a, b) => a.id - b.id),
    excludedMemberIds: [...excludedMemberIds].sort((a, b) => a - b),
    attachmentUrls: readyAttachments.map((a) => annotateAttachmentUrl(a.objectPath ?? "", a.name)),
    scheduledFor,
    submissionRevision,
  }));

  const broadcastMutation = useMutation({
    retry: false,
    mutationFn: async () => {
      const sentBody = messageText;
      const attachmentUrls = readyAttachments
        .map((a) => annotateAttachmentUrl(a.objectPath ?? "", a.name))
        .filter((u) => !!u);
      const controller = abort.begin();
      ambiguousSubmissionRef.current = true;
      try {
        return await boundedBroadcastRequest(controller, async () => {
        // An ambiguous retry must use the exact original payload, including
        // roster consent. A background roster refresh must not change it.
        if (submissionBodyRef.current?.id !== clientRequestId) {
          // Keep the exact draft available for an explicit roster-review
          // resubmission if the durable worker refuses it without sending.
          restoreSubmittedDraftRef.current = () => {
            setSelectedPropertyIds(selectedPropertyIds);
            setSelectedMemberIds(selectedMemberIds);
            setMessageText(messageText);
            setAudienceMode(audienceMode);
            setAttachments(attachments);
            setScheduledFor(scheduledFor);
            selectionInitializedRef.current = true;
            previousDirectoryRef.current = members;
            setFinalReviewOpen(true);
            setFinalConfirmation(null);
            setSubmissionRevision((revision) => revision + 1);
            setReviewing(true);
            onOpenChange(true);
          };
          submissionBodyRef.current = { id: clientRequestId, body: {
            clientRequestId,
            audienceMode,
            memberIds: validSelectedIds,
            reviewedRecipients,
            excludedMemberIds,
            message: sentBody,
            propertyIds: selectedPropertyIds,
            ...(attachmentUrls.length > 0 ? { attachmentUrls } : {}),
            ...(scheduledFor ? { scheduledFor } : {}),
            acknowledgeRosterGaps: rosterReview.acknowledged,
            ...(rosterReview.data ? { acknowledgedRosterFingerprint: rosterReview.data.fingerprint } : {}),
          } };
        }
        const res = await apiRequest(
          "POST",
          "/api/app/messages/broadcast",
          // Send the chosen houses too. The server re-derives every recipient's
          // property from the database and refuses anyone outside this set, so
          // a stale or mis-resolved picker can't deliver to another house.
          submissionBodyRef.current.body,
          { signal: controller.signal },
        );
        const data = await res.json() as { sent: number; queued: number; broadcastId: string };
        if (!data.broadcastId) throw new Error("No broadcast receipt was returned. Check Recent broadcasts before sending a changed draft.");
        return { data };
        });
      } finally {
        abort.clear(controller);
      }
    },
    onSuccess: ({ data }) => {
      if (!isMountedRef.current) return;
      ambiguousSubmissionRef.current = false;
      const broadcastId = data.broadcastId;
      submittedBroadcastIdRef.current = broadcastId;
      setLastBroadcastId(broadcastId);
      const statusAction = broadcastId ? (
        <ToastAction altText="View delivery status" onClick={() => setStatusBroadcastId(broadcastId)} data-testid="toast-action-broadcast-status">
          View status
        </ToastAction>
      ) : undefined;
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      // Also refresh any open conversation timeline so the pink broadcast
      // bubble appears immediately instead of waiting out the 10s timeline
      // poll. Prefix key — invalidates every member's timeline.
      queryClient.invalidateQueries({ queryKey: ["/api/app/members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/broadcasts/recent"] });
      toast({
        title: scheduledFor ? "Broadcast scheduled" : "Broadcast queued",
        description: `${data.queued ?? 0} recipients queued, not yet confirmed sent. You can leave this screen; delivery continues in the background.`,
        action: statusAction,
      });
      handleClose();
      setStatusBroadcastId(broadcastId);
    },
    onError: (err: any) => {
      if (isAbortError(err)) return;
      if (isPadsplitSendError(err)) {
        setPadsplitErrorOpen(true);
        return;
      }
      if (/RECIPIENT_REVIEW_CHANGED|ROSTER_GAPS_(CHANGED|UNACKNOWLEDGED)/.test(String(err?.message ?? ""))) {
        ambiguousSubmissionRef.current = false;
        submissionBodyRef.current = null;
        setFinalReviewOpen(true);
        setFinalConfirmation(null);
        void membersQuery.refetch();
        setSubmissionRevision((revision) => revision + 1);
        // The roster moved since review — nothing was sent. Re-run the check
        // (a new fingerprint clears the acknowledgement) and keep the draft.
        setReviewing(true);
        rosterReview.keepWaiting();
        toast({
          title: "Nothing was sent — the roster changed",
          description: parseErrorMessage(err, "Review the updated roster check and send again."),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Broadcast submission not confirmed",
        description: `${parseErrorMessage(err, "The request result is unknown.")} Check Recent broadcasts. Retry this unchanged draft to recover the same job; editing it starts a new broadcast. Do not send a manual copy until you know its status.`,
        variant: "destructive",
      });
    },
  });

  function handleClose() {
    onOpenChange(false);
    setReviewing(false);
    // Closing a request is NOT cancelling a durable send. Preserve ambiguous
    // drafts and their UUID so reopening/retrying cannot create a second job.
    if (ambiguousSubmissionRef.current) return;
    setSelectedPropertyIds([]);
    setSelectedMemberIds([]);
    setPropertySearch("");
    setMemberSearch("");
    setMessageText("");
    setAudienceMode("whole_house");
    selectionInitializedRef.current = false;
    previousDirectoryRef.current = [];
    setFinalReviewOpen(false);
    setFinalConfirmation(null);
    setAttachments([]);
    setScheduleOpen(false);
    setScheduleDate("");
    setScheduleTime("");
    setScheduleError("");
    setScheduledFor(null);
    setReviewing(false);
    setLastBroadcastId(null);
  }

  const selectedPropertySet = useMemo(() => new Set(selectedPropertyIds), [selectedPropertyIds]);
  const selectedMemberSet = useMemo(() => new Set(selectedMemberIds), [selectedMemberIds]);
  const { handleItemClick: handlePropertyClick } = useRangeSelection(
    visibleProperties.map((p) => p.id),
    selectedPropertySet,
    (next) => setSelectedPropertyIds(Array.from(next)),
    propertySearch,
  );
  const { handleItemClick: handleCurrentMemberClick } = useRangeSelection(
    // Conflicted members are excluded from every bulk selection path — the
    // send route refuses them, so a shift-range that swept one in would just
    // produce a failure row.
    selectableMemberIds,
    selectedMemberSet,
    (next) => setSelectedMemberIds(Array.from(next)),
    memberSearch,
  );
  function handleMemberClick(id: number, shift: boolean) {
    const member = members.find((row) => row.id === id);
    if (!member || !selectableRecipient(member)) return;
    if (member.group === "current") handleCurrentMemberClick(id, shift);
    else toggleMember(id); // Needs-review consent is individual, never ranged.
  }

  // "Select all"/"Deselect all" operates on the currently visible (filtered)
  // properties, merging with or subtracting from selections made under other
  // searches so filtering never silently drops a selection.
  const visiblePropertyIds = useMemo(() => visibleProperties.map((p) => p.id), [visibleProperties]);
  const allVisibleSelected =
    visiblePropertyIds.length > 0 &&
    visiblePropertyIds.every((id) => selectedPropertySet.has(id));

  function toggleAllProperties() {
    if (allVisibleSelected) {
      const visibleSet = new Set(visiblePropertyIds);
      setSelectedPropertyIds((prev) => prev.filter((id) => !visibleSet.has(id)));
    } else {
      setSelectedPropertyIds((prev) => Array.from(new Set([...prev, ...visiblePropertyIds])));
    }
  }

  function toggleMember(id: number) {
    setSelectedMemberIds(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  }

  // "Select all"/"Deselect all" operates on the currently visible (filtered)
  // members, merging with or subtracting from selections made under other
  // searches so filtering never silently drops a selection.
  const visibleMemberIds = useMemo(() => selectableMemberIds, [selectableMemberIds]);
  const allVisibleMembersSelected =
    visibleMemberIds.length > 0 &&
    visibleMemberIds.every((id) => selectedMemberSet.has(id));

  function toggleAllMembers() {
    if (allVisibleMembersSelected) {
      const visibleSet = new Set(visibleMemberIds);
      setSelectedMemberIds((prev) => prev.filter((id) => !visibleSet.has(id)));
    } else {
      setSelectedMemberIds((prev) => Array.from(new Set([...prev, ...visibleMemberIds])));
    }
  }

  // When exactly one recipient is selected we know their real details, so the
  // preview can substitute their values instead of the generic samples. Room
  // number isn't part of the broadcast member payload, so it falls back to the
  // sample value in the preview.
  const previewRecipient = useMemo<{ name: string; context: Partial<MessageVarContext> } | null>(() => {
    if (validSelectedIds.length !== 1) return null;
    const m = members.find((mm) => mm.id === validSelectedIds[0]);
    if (!m) return null;
    const prop = m.propertyId ? properties.find((p) => p.id === m.propertyId) : null;
    const firstName = m.name.trim().split(/\s+/)[0] || m.name;
    return {
      name: m.name,
      context: {
        memberFirstName: firstName,
        memberFullName: m.name,
        propertyName: prop?.name ?? null,
        propertyFullAddress: prop?.address ?? null,
      },
    };
  }, [validSelectedIds, members, properties]);

  const canSend =
    validSelectedIds.length > 0 &&
    messageText.trim().length > 0 &&
    !broadcastMutation.isPending &&
    !isImproving &&
    !hasUploading &&
    !hasAttachmentErrors &&
    !membersQuery.isFetching &&
    !membersQuery.isError;

  // Persist first. The worker owns bounded roster confirmation and delivery.
  // Only an explicit review/resubmission after a refused job uses the
  // independent client roster review; that path never auto-sends.
  const canConfirmSend = canSend && finalConfirmed && (!reviewing || rosterReview.canSend);
  function handlePrimarySend() {
    if (!canSend || messagingOutage) return;
    if (!finalReviewOpen) {
      setFinalReviewOpen(true);
      setFinalConfirmation(null);
      return;
    }
    if (canConfirmSend) broadcastMutation.mutate();
  }

  // Draft-only work still needs protection from accidental dismissal. Never
  // gate dismissal on the resident check or a persisted broadcast job.
  const blockDraftDismissal = hasUploading || isImproving;
  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && blockDraftDismissal) return;
        if (!o) handleClose();
      }}
    >
      <DialogContent
        className="max-w-[41.6rem] max-h-[90vh] overflow-y-auto grid-cols-[minmax(0,1fr)]"
        data-testid="modal-whole-house-broadcast"
        onInteractOutside={(e) => { if (blockDraftDismissal) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (blockDraftDismissal) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (blockDraftDismissal) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
            <DialogTitle>Message Whole House</DialogTitle>
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setRecentOpen(true)}
              data-testid="button-recent-broadcasts"
            >
              Recent broadcasts
            </button>
          </div>
          <DialogDescription>
            Select properties, then choose which members to message.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset className="min-w-0 rounded-md border p-3 space-y-2">
            <legend className="px-1 text-sm font-medium">Who should receive this message?</legend>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="broadcast-audience" value="whole_house" checked={audienceMode === "whole_house"} onChange={() => setAudienceMode("whole_house")} />
              <span>Whole house — check the complete roster and preserve your exclusions.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="broadcast-audience" value="selected_residents" checked={audienceMode === "selected_residents"} onChange={() => setAudienceMode("selected_residents")} />
              <span>Selected residents only — verify only the people you select; never add others.</span>
            </label>
          </fieldset>
          {selectedPropertyIds.length > 0 && !reviewing && (
            <div className="rounded-md border p-3 text-sm space-y-1" role="status" data-testid="broadcast-preflight">
              <div className="font-medium">Resident check</div>
              <p>{membersQuery.isFetching ? "Loading residents…" : residencyPending ? "Verifying current residents. You can select eligible local records while the check runs." : residencyUnavailable ? "Live verification is currently unavailable. You can select eligible local records, but sending still requires a successful resident check." : rosterReview.confirming ? "Checking residents while you compose…" : rosterReview.confirmed ? "Resident check passed. Membership and access will be checked again before sending." : rosterReview.message ?? "Select residents to check their current membership."}</p>
              <Button type="button" size="sm" variant="outline" disabled={membersQuery.isFetching}
                onClick={() => { residencyPollingStartedAt.current = Date.now(); void membersQuery.refetch(); }}>
                Refresh resident evidence
              </Button>
              {!rosterReview.confirming && validSelectedIds.length > 0 && (
                <Button type="button" size="sm" variant="outline" onClick={rosterReview.keepWaiting}>Check again</Button>
              )}
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Select Properties</Label>
              {properties.length > 1 && (
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={toggleAllProperties}
                  data-testid="button-select-all-properties"
                >
                  {allVisibleSelected ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            {properties.length > 1 && (
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  placeholder="Search by name or address..."
                  className="pl-8 pr-8 h-9 text-sm"
                  data-testid="input-property-search"
                />
                {propertySearch && (
                  <button
                    type="button"
                    onClick={() => setPropertySearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                    data-testid="button-clear-property-search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            {propertiesQuery.isLoading ? (
              <div className="text-sm text-gray-400 py-2">Loading properties...</div>
            ) : properties.length === 0 ? (
              <div className="text-sm text-gray-400 py-2">No properties found.</div>
            ) : visibleProperties.length === 0 ? (
              <div className="text-sm text-gray-400 py-2" data-testid="text-no-property-matches">
                No properties match "{propertySearch.trim()}".
              </div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2" data-testid="property-checkbox-list">
                {visibleProperties.map(p => (
                  <div
                    key={p.id}
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); handlePropertyClick(p.id, e.shiftKey); }}
                  >
                    <Checkbox
                      checked={selectedPropertyIds.includes(p.id)}
                    aria-label={`Include ${p.name || p.address || `property ${p.id}`}`}
                    onCheckedChange={() => {}}
                      data-testid={`checkbox-property-${p.id}`}
                    />
                    <span className="text-sm select-none">
                      {p.name || p.address || `Property #${p.id}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedPropertyIds.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Members</Label>
                {members.length > 1 && (
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={toggleAllMembers}
                    data-testid="button-select-all-members"
                  >
                    {allVisibleMembersSelected ? "Deselect visible current" : "Select visible current"}
                  </button>
                )}
              </div>

              {members.length > 1 && (
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <Input
                    aria-label="Search broadcast residents"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search members by name..."
                    className="pl-8 pr-8 h-9 text-sm"
                    data-testid="input-member-search"
                  />
                  {memberSearch && (
                    <button
                      type="button"
                      onClick={() => setMemberSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                      data-testid="button-clear-member-search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {membersQuery.isLoading ? (
                <div className="text-sm text-gray-400 py-2">Loading members...</div>
              ) : members.length === 0 ? (
                <div className="text-sm text-gray-400 py-2">No residents on file at selected properties.</div>
              ) : (
                <BroadcastRecipientPicker rows={members} search={memberSearch} selectedIds={selectedMemberSet} onToggle={handleMemberClick} />
              )}
            </div>
          )}

          {messagingOutage && <MessagingOutageBanner />}

          {!messagingOutage && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="broadcast-message" className="text-sm font-medium">
                Message
              </Label>
              <div className="flex items-center gap-2">
                {aiDraftingAllowed && !isImproving && messageText.trim().length > 0 && (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5 shrink-0"
                    title="Rewrite this broadcast in the company house voice, using placeholders so it reads correctly for every recipient"
                    data-testid="button-broadcast-improve-ai"
                    onClick={() => improveMutation.mutate(messageTextareaRef.current?.value ?? messageText)}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Improve with AI
                  </Button>
                )}
                {isImproving && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled
                    className="text-purple-400 border-purple-200 gap-1.5 shrink-0"
                    data-testid="button-broadcast-improve-ai-thinking"
                  >
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Thinking…
                  </Button>
                )}
                {preImproveText !== null && !isImproving && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-purple-700 hover:text-purple-900 hover:bg-purple-50 px-2 shrink-0"
                    title="Undo AI improvement and restore your original text"
                    data-testid="button-broadcast-undo-improve-ai"
                    onClick={() => {
                      const prev = preImproveText;
                      setPreImproveText(null);
                      setMessageText(prev);
                    }}
                  >
                    Undo
                  </Button>
                )}
                <InsertPlaceholderDropdown onInsert={insertAtCursor} />
              </div>
            </div>
            <Textarea
              ref={messageTextareaRef}
              id="broadcast-message"
              placeholder="Type your message here..."
              value={messageText}
              onChange={e => {
                setMessageText(e.target.value);
                if (preImproveText !== null) setPreImproveText(null);
              }}
              disabled={isImproving}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  e.preventDefault();
                  handlePrimarySend();
                }
              }}
              rows={4}
              className="resize-none min-h-[106px]"
              data-testid="textarea-broadcast-message"
            />
            <MessagePlaceholderPreview
              content={messageText}
              recipientContext={previewRecipient?.context}
              recipientName={previewRecipient?.name}
            />
            {/* Attachment list */}
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="broadcast-attachment-list">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className={`flex items-center gap-2 rounded border px-2 py-1 text-sm ${
                      a.status === "error"
                        ? "border-red-200 bg-red-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                    data-testid={`broadcast-attachment-${a.id}`}
                  >
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <span className="flex-1 truncate text-gray-700">{a.name}</span>
                    <span className="shrink-0 text-xs text-gray-400">{formatBytes(a.size)}</span>
                    {a.status === "uploading" && (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" />
                    )}
                    {a.status === "error" && (
                      <span className="shrink-0 text-xs text-red-600">{a.errorMessage}</span>
                    )}
                    <button
                      type="button"
                      className="shrink-0 text-gray-400 hover:text-gray-600"
                      onClick={() => removeAttachment(a.id)}
                      aria-label={`Remove ${a.name}`}
                      data-testid={`button-broadcast-remove-attachment-${a.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {lastBroadcastId && <Button variant="outline" onClick={() => setStatusBroadcastId(lastBroadcastId)} data-testid="button-broadcast-view-status">View delivery status</Button>}

          {membersQuery.isError && (
            <div className="flex items-center gap-2 text-sm text-red-700" data-testid="broadcast-members-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Couldn't load members for these houses, so sending is blocked.
              <Button type="button" variant="outline" size="sm" onClick={() => void membersQuery.refetch()}>Try again</Button>
            </div>
          )}

          {!reviewing && selectedPropertyIds.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              disabled={broadcastMutation.isPending || ambiguousSubmissionRef.current}
              onClick={() => setReviewing(true)}
              data-testid="button-broadcast-review-residents"
            >
              Review residents before resubmitting
            </Button>
          )}
          {reviewing && (
            <BroadcastRosterReviewPanel
              review={rosterReview}
              statusFilteredMemberIds={statusFilteredMemberIds}
              onBack={() => setReviewing(false)}
            />
          )}
          {finalReviewOpen && (
            <BroadcastFinalRecipientReview
              rows={selectedRecipients}
              confirmed={finalConfirmed}
              onConfirm={(confirmed) => setFinalConfirmation(confirmed ? finalReviewKey : null)}
              onBack={() => { setFinalReviewOpen(false); setFinalConfirmation(null); }}
            />
          )}

          {/* Hidden file input for attachment picker */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={Array.from(ALLOWED_ATTACHMENT_MIME).join(",")}
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="input-broadcast-attachment"
          />

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {/* Left side: attachment + schedule controls */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title={`Attach a file (${ATTACHMENT_MAX_COUNT - attachments.length} remaining)`}
                disabled={attachments.length >= ATTACHMENT_MAX_COUNT || broadcastMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
                data-testid="button-broadcast-attach"
              >
                <Paperclip className="h-4 w-4" />
              </Button>

              <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant={scheduledFor ? "default" : "outline"}
                    size="icon"
                    title="Schedule for later"
                    disabled={broadcastMutation.isPending}
                    data-testid="button-broadcast-schedule-open"
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="start">
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Schedule broadcast</p>
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">Date</label>
                      <input
                        type="date"
                        className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        data-testid="input-broadcast-schedule-date"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">Time</label>
                      <input
                        type="time"
                        className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        data-testid="input-broadcast-schedule-time"
                      />
                    </div>
                    {scheduleError && (
                      <p className="text-xs text-red-600" data-testid="text-broadcast-schedule-error">{scheduleError}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={handleScheduleConfirm}
                        data-testid="button-broadcast-schedule-confirm"
                      >
                        Set Schedule
                      </Button>
                      {scheduledFor && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setScheduledFor(null); setScheduleDate(""); setScheduleTime(""); setScheduleOpen(false); }}
                          data-testid="button-broadcast-schedule-clear"
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {scheduledFor && (
                <span className="text-xs text-muted-foreground" data-testid="text-broadcast-scheduled-time">
                  Scheduled {new Date(scheduledFor).toLocaleString()}
                </span>
              )}
            </div>

            {/* Right side: cancel + send buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                data-testid="button-broadcast-cancel"
              >
                Close
              </Button>
              {!messagingOutage && (
                <Button
                  onClick={handlePrimarySend}
                  disabled={finalReviewOpen ? !canConfirmSend : !canSend}
                  title={finalReviewOpen ? "Submit reviewed broadcast (Ctrl+Enter)" : "Review exact recipients before submitting (Ctrl+Enter)"}
                  data-testid="button-broadcast-send"
                >
                  <Send className="h-4 w-4 mr-1.5" />
                  {broadcastMutation.isPending
                    ? "Saving broadcast..."
                    : !finalReviewOpen
                      ? `Review ${validSelectedIds.length} recipients`
                      : (scheduledFor ? `Schedule reviewed broadcast to ${validSelectedIds.length}` : `Send reviewed broadcast to ${validSelectedIds.length}`)}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      <PadsplitSendErrorDialog open={padsplitErrorOpen} onOpenChange={setPadsplitErrorOpen} />
    </Dialog>
    <RecentBroadcastsDialog
      open={recentOpen}
      onOpenChange={setRecentOpen}
      onSelect={(id) => { setRecentOpen(false); setStatusBroadcastId(id); }}
    />
    <BroadcastStatusDialog
      broadcastId={statusBroadcastId}
      onClose={() => setStatusBroadcastId(null)}
      onReviewRoster={statusBroadcastId === submittedBroadcastIdRef.current && restoreSubmittedDraftRef.current
        ? () => {
          setStatusBroadcastId(null);
          restoreSubmittedDraftRef.current?.();
        }
        : undefined}
    />
    </>
  );
}
